/**
 * Custom element for the cart drawer rewards progress bar.
 *
 * Animates the progress fill smoothly between cart updates (sticky behaviour):
 * the bar transitions from its previous value to the new one instead of
 * restarting from zero on every cart change.
 *
 * Survives both cart re-render paths used by the theme:
 *   1. In-place: cart.js publishes `cartUpdate` after a remote change. The
 *      element is still in the DOM; it reads the new total and updates the
 *      CSS custom property — the CSS transition does the easing.
 *   2. DOM swap: cart-drawer.js replaces `.drawer__inner` innerHTML when the
 *      change originates in the drawer itself. The element is rebuilt; on
 *      `connectedCallback` it reads the last applied progress from
 *      sessionStorage, seeds it as the starting value, and transitions to the
 *      server-rendered target on the next frame.
 *
 * Globals expected (loaded before this file via theme.liquid):
 *   - `PUB_SUB_EVENTS`  (assets/constants.js)
 *   - `subscribe` / `publish` (assets/pubsub.js)
 */

(function () {
  'use strict';

  const STORAGE_KEY = 'bur-rewards-progress';
  const FILL_SELECTOR = '.cart-drawer__rewards-fill';
  const BAR_SELECTOR = '.cart-drawer__rewards-bar';
  const TITLE_SELECTOR = '.cart-drawer__rewards-title';
  const UNLOCKED_MODIFIER = 'cart-drawer__rewards-bar--unlocked';

  /**
   * Clamp a number into the [min, max] range.
   * @param {number} value
   * @param {number} min
   * @param {number} max
   * @returns {number}
   */
  const clamp = (value, min, max) => {
    if (value < min) return min;
    if (value > max) return max;
    return value;
  };

  /**
   * Compute progress (0..1) given current and threshold totals in cents.
   * Returns 0 when the threshold is missing or non-positive.
   */
  const computeProgress = (currentCents, thresholdCents) => {
    if (!thresholdCents || thresholdCents <= 0) return 0;
    return clamp(currentCents / thresholdCents, 0, 1);
  };

  /**
   * Read an integer attribute, defaulting to 0 when missing or invalid.
   */
  const readIntAttr = (element, attr) => {
    const parsed = parseInt(element.getAttribute(attr), 10);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  /**
   * Persist the last applied progress so we can resume from it after a
   * destroy-and-rebuild cycle. Fails silently when storage is unavailable.
   */
  const persistProgress = (progress) => {
    try {
      sessionStorage.setItem(STORAGE_KEY, String(progress));
    } catch (_err) {
      /* storage disabled (private mode, quota); not critical */
    }
  };

  /**
   * Read the persisted progress; returns 0 when missing or invalid.
   */
  const readPersistedProgress = () => {
    try {
      const value = parseFloat(sessionStorage.getItem(STORAGE_KEY));
      return Number.isFinite(value) ? clamp(value, 0, 1) : 0;
    } catch (_err) {
      return 0;
    }
  };

  class RewardsBar extends HTMLElement {
    constructor() {
      super();
      this.handleCartUpdate = this.handleCartUpdate.bind(this);
      this.cartUpdateUnsubscriber = null;
    }

    connectedCallback() {
      this.thresholdCents = readIntAttr(this, 'data-threshold-cents');
      this.fillElement = this.querySelector(FILL_SELECTOR);
      this.barElement = this.querySelector(BAR_SELECTOR);
      this.titleElement = this.querySelector(TITLE_SELECTOR);
      this.progressMessage = this.getAttribute('data-progress-message') || '';
      this.unlockedMessage = this.getAttribute('data-unlocked-message') || '';

      if (!this.fillElement || this.thresholdCents <= 0) return;

      const currentCents = readIntAttr(this, 'data-current-cents');
      const targetProgress = computeProgress(currentCents, this.thresholdCents);

      // Replay the transition from the last persisted value so the bar grows
      // (or shrinks) visibly even when the host element is freshly recreated
      // after a DOM swap.
      this.replayTransition(readPersistedProgress(), targetProgress);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate);
      }
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
        this.cartUpdateUnsubscriber = null;
      }
    }

    /**
     * Apply `from` instantly, then schedule `to` for the next paint frame.
     * Two RAFs guarantee the browser commits the starting value before the
     * style change, so the CSS transition observes a real delta and animates.
     */
    replayTransition(from, to) {
      this.fillElement.style.setProperty('--rewards-progress', from);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.applyProgress(to);
        });
      });
    }

    /**
     * Update the fill width and the unlocked state in place.
     * Persists progress so a later remount can replay from this point.
     * @param {number} progress 0..1
     */
    applyProgress(progress) {
      const clamped = clamp(progress, 0, 1);
      this.fillElement.style.setProperty('--rewards-progress', clamped);
      persistProgress(clamped);

      if (!this.barElement) return;
      const unlocked = clamped >= 1;
      this.barElement.classList.toggle(UNLOCKED_MODIFIER, unlocked);

      if (this.titleElement) {
        const nextMessage = unlocked ? this.unlockedMessage : this.progressMessage;
        if (nextMessage && this.titleElement.innerHTML !== nextMessage) {
          this.titleElement.innerHTML = nextMessage;
        }
      }
    }

    /**
     * React to cart updates published by cart.js (cart-items or other sources).
     * When the change originates from the drawer itself the element is rebuilt
     * instead — that flow is handled in connectedCallback.
     */
    handleCartUpdate(data) {
      if (!data || !data.cartData) return;

      const itemCount = parseInt(data.cartData.item_count, 10);
      if (itemCount === 0) {
        this.setAttribute('hidden', '');
        return;
      }
      this.removeAttribute('hidden');

      const totalCents = parseInt(data.cartData.total_price, 10);
      if (!Number.isFinite(totalCents)) return;
      this.applyProgress(computeProgress(totalCents, this.thresholdCents));
    }
  }

  if (!customElements.get('rewards-bar')) {
    customElements.define('rewards-bar', RewardsBar);
  }
})();
